import { useState, useCallback } from "react";
import axios from "axios";
import { ServerResponse } from "../../../types";
import { useLatestRef } from "../../../hooks/useLatestRef";

function defaultErrorMessage(error: Error, fallback: string): string {
  if (axios.isAxiosError<ServerResponse<string>>(error)) {
    const serverError = error.response?.data?.payload?.results;
    if (typeof serverError === "string" && serverError !== "") {
      return serverError;
    }
  }
  return error.message || fallback;
}

export interface UsePaymentCompletionOptions<TResponse> {
  updateCache: (response: TResponse) => Promise<void> | void;
  onSuccess?: (response: TResponse) => void;
  formatError?: (error: Error) => string;
  fallbackMessage?: string;
}

export interface UsePaymentCompletionResult<TResponse> {
  /**
   * Runs through the lifecycle. On failure, sets `error` and rethrows so callers
   * can clean up one-shot latches. Use `runAndSwallow` if you have nothing to do
   * on failure beyond surfacing the error message.
   */
  run: (createRequest: () => Promise<TResponse>) => Promise<TResponse>;
  runAndSwallow: (createRequest: () => Promise<TResponse>) => void;
  isLoading: boolean;
  error: string | undefined;
  setError: (message: string | undefined) => void;
}

export function usePaymentCompletion<TResponse>(
  options: UsePaymentCompletionOptions<TResponse>,
): UsePaymentCompletionResult<TResponse> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Stash latest options in a ref so `run` keeps a stable identity across
  // renders even when callers pass inline closures.
  const optionsRef = useLatestRef(options);

  const run = useCallback(
    async (createRequest: () => Promise<TResponse>) => {
      const { updateCache, onSuccess, formatError, fallbackMessage = "An error occurred during purchase" } =
        optionsRef.current;
      setError(undefined);
      setIsLoading(true);
      try {
        const response = await createRequest();
        await updateCache(response);
        onSuccess?.(response);
        return response;
      } catch (caught) {
        const err = caught as Error;
        const message = formatError
          ? formatError(err)
          : defaultErrorMessage(err, fallbackMessage);
        setError(message);
        throw caught;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const runAndSwallow = useCallback(
    (createRequest: () => Promise<TResponse>) => {
      void run(createRequest).catch(() => {});
    },
    [run],
  );

  return { run, runAndSwallow, isLoading, error, setError };
}
